<template>
  <div
    class="fade-in h-full overflow-y-auto transition-all duration-200 max-w-screen-xl w-full mx-auto"
  >
    <div class="p-1 md:p-4 transition-all duration-200">
      <div class="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <div class="flex-shrink-0">
          <h1 class="text-xl font-normal flex items-center">
            <UserCog class="w-5 h-5 mr-2" />
            Expeditions management
          </h1>
        </div>

        <div class="flex items-center gap-2 mt-2 sm:mt-0 sm:ml-auto">
          <FellowSearch />

          <router-link
            v-if="isAdmin"
            to="/manage/create-pod"
            class="flex-1 sm:flex-initial bg-cosmicPurple text-white px-4 py-2 rounded-md flex items-center justify-center gap-1 hover:bg-cosmicPurple/80 hover:-translate-y-[1px] transition-all duration-200 text-sm"
          >
            <Plus class="w-4 h-4" />
            <span>Create new Pod</span>
          </router-link>
        </div>
      </div>

      <!-- Tab bar -->
      <div class="flex border-b border-gray-200 mb-4">
        <!-- Pods Tab -->
        <button
          class="px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
          :class="
            activeTab === 'pod'
              ? 'text-blue-600 border-blue-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          "
          @click="switchToPod"
        >
          Pods
          <span
            v-if="podsStore.pods.length"
            class="ml-1 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full"
          >
            {{ podsStore.pods.length }}
          </span>
        </button>
        <!-- Expeditions Tab -->
        <button
          class="px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
          :class="
            activeTab === 'expeditions'
              ? 'text-blue-600 border-blue-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          "
          @click="switchToExpeditions"
        >
          Expeditions
        </button>
      </div>

      <!-- Pods Tab -->
      <div v-if="activeTab === 'pod'">
        <PodListTable />
      </div>

      <!-- Loading Skeleton Table -->
      <div v-else-if="loading" class="animate-pulse">
        <div class="bg-gray-200 h-8 w-full mb-4 rounded"></div>
        <div
          v-for="n in 3"
          :key="`skeleton-${n}`"
          class="bg-gray-100 h-12 w-full mb-2 rounded"
        ></div>
      </div>

      <!-- Expedition Table -->
      <div v-else>
        <!-- Mobile Card View -->
        <div class="md:hidden space-y-4">
          <div
            v-for="expedition in sortedExpeditions"
            :key="expedition.id"
            @click="navigateToExpedition(expedition.id)"
            class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
          >
            <div class="flex items-center justify-between mb-3">
              <div class="text-sm font-medium text-gray-900">
                {{ formatExpeditionName(expedition.name) }}
              </div>
              <div class="text-xs text-gray-500">
                {{ formatDate(expedition.start_date) }}
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div class="text-xs text-gray-500 mb-1">Current Week</div>
                <div class="text-sm">
                  <template v-if="expedition.current_week >= expedition.total_weeks">
                    <span
                      class="px-2 inline-flex text-xs leading-5 rounded-full bg-cosmicPurple/10 text-cosmicPurple"
                    >
                      Complete ({{ expedition.current_week }}/{{ expedition.total_weeks }})
                    </span>
                  </template>
                  <template v-else>
                    {{ expedition.current_week }} / {{ expedition.total_weeks }}
                  </template>
                </div>
              </div>
              <div>
                <div class="text-xs text-gray-500 mb-1">Adventurers</div>
                <div class="text-sm">{{ expedition.adventurers.length || 'None' }}</div>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div class="flex -space-x-2 overflow-hidden">
                <template v-for="adventurer in expedition.adventurers" :key="adventurer.id">
                  <div
                    v-if="adventurer.avatar_image"
                    class="inline-block h-8 w-8 rounded-full ring-2 ring-white overflow-hidden"
                  >
                    <img
                      class="h-full w-full object-cover"
                      :class="{ 'blur-md select-none': isBlurred }"
                      :src="adventurer.avatar_image"
                      :alt="`${adventurer.first_name} ${adventurer.last_name}`"
                    />
                  </div>
                  <span
                    v-else
                    class="inline-flex items-center justify-center h-8 w-8 rounded-full ring-2 ring-white bg-gray-200 overflow-hidden"
                  >
                    <span
                      class="inline-flex items-center justify-center h-full w-full"
                      :class="{ 'blur-md select-none': isBlurred }"
                    >
                      <User class="h-5 w-5 text-gray-500" />
                    </span>
                  </span>
                </template>
              </div>

              <div class="flex items-center">
                <component
                  :is="isBlurred ? 'div' : 'router-link'"
                  @click.stop
                  v-if="expedition.main_guide"
                  :to="isBlurred ? undefined : `/profile/${expedition.main_guide.username}`"
                  class="relative"
                >
                  <div
                    v-if="expedition.main_guide.avatar_image"
                    class="inline-block h-8 w-8 rounded-full ring-2 ring-white hover:scale-105 transition-all duration-200 overflow-hidden"
                  >
                    <img
                      class="h-full w-full object-cover"
                      :class="{ 'blur-md select-none': isBlurred }"
                      :src="expedition.main_guide.avatar_image"
                      :alt="`${expedition.main_guide.first_name} ${expedition.main_guide.last_name}`"
                    />
                  </div>
                  <span
                    v-else
                    class="inline-flex items-center justify-center h-8 w-8 rounded-full ring-2 ring-white bg-gray-200 overflow-hidden"
                  >
                    <span
                      class="inline-flex items-center justify-center h-full w-full"
                      :class="{ 'blur-md select-none': isBlurred }"
                    >
                      <User class="h-5 w-5 text-gray-500" />
                    </span>
                  </span>

                  <!-- Employer badges -->
                  <div
                    v-if="isAdmin && getMainGuideEmployers(expedition.main_guide.email).length"
                    class="absolute -bottom-1 -right-1"
                  >
                    <template v-if="getMainGuideEmployers(expedition.main_guide.email).length <= 2">
                      <!-- 1-2 badges: horizontal layout -->
                      <div class="flex -space-x-1">
                        <img
                          v-for="employer in getMainGuideEmployers(expedition.main_guide.email)"
                          :key="employer.name"
                          :src="employer.logo"
                          :alt="employer.name"
                          class="w-4 h-4 rounded-full border border-white bg-white object-contain hover:scale-105 transition-transform duration-150"
                          :title="employer.name"
                        />
                      </div>
                    </template>
                    <template v-else>
                      <!-- 3 badges: pyramid layout -->
                      <div class="relative translate-x-0.5 translate-y-0.5">
                        <!-- Top badge -->
                        <div class="flex justify-center -mb-1">
                          <img
                            :src="getMainGuideEmployers(expedition.main_guide.email)[0].logo"
                            :alt="getMainGuideEmployers(expedition.main_guide.email)[0].name"
                            class="w-4 h-4 rounded-full border border-white bg-white object-contain hover:scale-105 transition-transform duration-150"
                            :title="getMainGuideEmployers(expedition.main_guide.email)[0].name"
                          />
                        </div>
                        <!-- Bottom two badges -->
                        <div class="flex -space-x-1">
                          <img
                            v-for="employer in getMainGuideEmployers(
                              expedition.main_guide.email
                            ).slice(1, 3)"
                            :key="employer.name"
                            :src="employer.logo"
                            :alt="employer.name"
                            class="w-4 h-4 rounded-full border border-white bg-white object-contain hover:scale-105 transition-transform duration-150"
                            :title="employer.name"
                          />
                        </div>
                      </div>
                    </template>
                  </div>
                </component>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop Table View -->
        <div class="hidden md:block">
          <ManageListTable
            :items="sortedExpeditions"
            users-column-label="Adventurers"
            empty-message="No expeditions found."
            :get-row-id="(e) => (e as Expedition).id"
            :get-row-url="(e) => `/manage/${(e as Expedition).id}`"
            :get-display-name="(e) => formatExpeditionName((e as Expedition).name)"
            :get-badge="(e) => getExpeditionBadge(e as Expedition)"
            :get-users="(e) => (e as Expedition).adventurers ?? []"
            :get-guide="(e) => (e as Expedition).main_guide ?? null"
            :get-start-date="(e) => (e as Expedition).start_date"
            @row-click="(e) => navigateToExpedition(String((e as Expedition).id))"
          />
        </div>
      </div>
      <DashboardFooter />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useExpeditionsStore } from '@/stores/expeditions'
import { usePodsStore } from '@/stores/pods'
import { User, Plus, UserCog } from 'lucide-vue-next'
import { formatDate } from '@/utils/formatDate'
import { formatExpeditionName } from '@/utils/formatExpeditionName'
import type { Expedition } from '@/types/expedition'
import FellowSearch from '@/components/manage/FellowSearch.vue'
import ManageListTable from '@/components/manage/ManageListTable.vue'
import PodListTable from '@/components/manage/campus/PodListTable.vue'
import DashboardFooter from '@/components/layout/DashboardFooter.vue'
import { useAuth } from '@/composables/useAuth'
import { usePrivacyMode } from '@/composables/usePrivacyMode'
import { useMainGuideEmployers } from '@/composables/useMainGuideEmployers'

const { isAdmin, isGuide } = useAuth()
const { getMainGuideEmployers } = useMainGuideEmployers()
const { isBlurred } = usePrivacyMode()
const router = useRouter()
const route = useRoute()
const expeditionsStore = useExpeditionsStore()
const podsStore = usePodsStore()
const loading = ref(true)
const activeTab = ref<'expeditions' | 'pod'>('pod')

function switchToExpeditions() {
  activeTab.value = 'expeditions'
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.replace({ query: { ...route.query, tab: 'expeditions' } })
}

function switchToPod() {
  activeTab.value = 'pod'
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.replace({ query: { ...route.query, tab: 'pod' } })
  if (podsStore.pods.length === 0) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    podsStore.fetchPods()
  }
}

// Function to handle row click navigation
const navigateToExpedition = (id: string) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push(`/manage/${id}`)
}

// Computed property to sort expeditions by date
const sortedExpeditions = computed(() => {
  // Return a sorted copy, don't mutate the original store state
  return [...expeditionsStore.expeditions].sort((a: Expedition, b: Expedition) => {
    const dateA = new Date(a.created_at).getTime()
    const dateB = new Date(b.created_at).getTime()
    return dateB - dateA // Sort in descending order (newest first)
  })
})

const getExpeditionBadge = (expedition: Expedition) => {
  const total = expedition.total_weeks ?? 0
  if (expedition.current_week >= total) {
    return {
      text: `Complete (${expedition.current_week}/${total})`,
      class:
        'px-2 inline-flex items-center text-sm leading-5 rounded-full bg-cosmicPurple/10 text-cosmicPurple'
    }
  }
  return {
    text: `Week ${expedition.current_week} / ${total}`,
    class: 'px-2 py-0.5 inline-flex items-center text-sm rounded-full bg-lime-100 text-lime-700'
  }
}

// Sync active tab with route query (tab=cohort redirects to tab=pod for backwards compatibility)
watch(
  () => route.query.tab,
  (tab) => {
    if (tab === 'pod' || tab === 'cohort') {
      if (tab === 'cohort') {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.replace({ query: { ...route.query, tab: 'pod' } })
      }
      activeTab.value = 'pod'
      if (podsStore.pods.length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        podsStore.fetchPods()
      }
    } else if (tab === 'expeditions') {
      activeTab.value = 'expeditions'
    }
  },
  { immediate: true }
)

onMounted(async () => {
  try {
    await expeditionsStore.fetchExpeditions()
  } finally {
    loading.value = false
  }
})
</script>
